import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  CheckCircle2,
  Phone,
  ShieldCheck,
  Award,
  Sun,
  Users,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { WaveDivider } from "@/components/WaveDivider";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useI18n } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";
const aboutHero = pic("about-hero");
const aboutCta = pic("about-cta");
import { pic } from "@/lib/responsive-images";

const amr3Rooftop = pic("amr3-technician-rooftop");
const whyExperience = pic("why-experience");
const whyLicensed = pic("why-licensed");
const whyNetwork = pic("why-network");

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About AMR 3 Design & Construction | 25+ Years in New Mexico" },
      {
        name: "description",
        content:
          "Owner-operated roofing and solar contractor in Albuquerque, NM. Over 25 years of integrated craftsmanship across New Mexico. Licensed, bonded and insured.",
      },
      { property: "og:title", content: "About AMR 3 Design & Construction" },
      {
        property: "og:description",
        content:
          "Owner-operated, fully integrated roofing and solar contractor with 25+ years of trusted craftsmanship across New Mexico.",
      },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-about.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-about.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "About AMR 3 Design & Construction" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-about.jpg" },
      { name: "twitter:title", content: "About AMR 3 Design & Construction" },
      { name: "twitter:description", content: "Owner-operated roofing and solar contractor with 25+ years of craftsmanship across New Mexico." },
      { property: "og:url", content: "https://www.amr3designconstruction.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/about" }],
  }),
  component: AboutPage,
});

function AboutPage() {
  const { t } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();


  const apart = ["1", "2", "3", "4", "5"];

  return (
    <>
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={aboutHero.src}
          srcSet={aboutHero.srcSet}
          sizes="100vw"
          alt="The AMR 3 Design & Construction team standing in front of a completed roof and solar installation in Albuquerque, NM"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.80)] via-[oklch(0.18_0.03_50/0.58)] to-[oklch(0.18_0.03_50/0.30)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.about") }]} />
          <p className="mb-4 inline-block rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
            {t("about.badge")}
          </p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-white drop-shadow sm:text-5xl lg:text-[3.25rem]">
            {t("about.h1")}
          </h1>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-background py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">{t("about.story.title")}</h2>
            <p className="mt-4 text-muted-foreground">{t("about.story.sub")}</p>
          </div>
          <div className="mt-10 grid items-center gap-12 md:grid-cols-2 lg:gap-16">
            <div className="relative overflow-hidden rounded-2xl shadow-[var(--shadow-elegant)]">
              <img
                src={amr3Rooftop.src}
                srcSet={amr3Rooftop.srcSet}
                sizes="(max-width: 768px) 100vw, 665px"
                alt="AMR 3 Design & Construction technician overlooking a New Mexico home with integrated roofing and solar at sunset"
                width={1448}
                height={1086}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-muted-foreground leading-relaxed">{t("about.story.paragraph")}</p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {[
                  "about.story.badges.years",
                  "about.story.badges.owner",
                  "about.story.badges.licensed",
                  "about.story.badges.bonded",
                  "about.story.badges.insured",
                ].map((key) => (
                  <span
                    key={key}
                    className="flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-2 text-xs font-semibold text-primary"
                  >
                    <BadgeCheck className="h-4 w-4 text-accent" /> {t(key)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      <div className="bg-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <hr className="border-border" />
        </div>
      </div>


      {/* CREDENTIALS STRIP — 25 yrs, Licensed 2022, Vetted network */}
      <section aria-labelledby="creds-title" className="bg-background pt-16 sm:pt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="creds-title" className="font-serif text-3xl font-bold text-primary sm:text-4xl">
              {t("about.creds.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {t("about.creds.sub")}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            <div className="overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent shadow-sm">
              <div className="aspect-[16/9] w-full overflow-hidden">
                <img
                  src={whyExperience.src}
          srcSet={whyExperience.srcSet}
          sizes="(max-width: 768px) 100vw, 33vw"
                  alt="AMR 3 crew with 25+ years of roofing and solar experience in New Mexico"
                  width={1024}
                  height={576}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Award className="h-6 w-6" aria-hidden />
                </div>
                <p className="mt-4 font-serif text-3xl font-bold text-primary">{t("about.creds.years")}</p>
                <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-accent">
                  {t("about.creds.yearsKicker")}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("about.creds.yearsBody")}
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent shadow-sm">
              <div className="aspect-[16/9] w-full overflow-hidden">
                <img
                  src={whyLicensed.src}
          srcSet={whyLicensed.srcSet}
          sizes="(max-width: 768px) 100vw, 33vw"
                  alt="Licensed, bonded and insured roofing and solar contractor in New Mexico"
                  width={1024}
                  height={576}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <BadgeCheck className="h-6 w-6" aria-hidden />
                </div>
                <p className="mt-4 font-serif text-3xl font-bold text-primary">{t("about.creds.licensed")}</p>
                <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-accent">
                  {t("about.creds.licensedKicker")}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("about.creds.licensedBody")}
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent shadow-sm">
              <div className="aspect-[16/9] w-full overflow-hidden">
                <img
                  src={whyNetwork.src}
          srcSet={whyNetwork.srcSet}
          sizes="(max-width: 768px) 100vw, 33vw"
                  alt="AMR 3's elite vetted network of licensed roofing and solar tradespeople in New Mexico"
                  width={1024}
                  height={576}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Users className="h-6 w-6" aria-hidden />
                </div>
                <p className="mt-4 font-serif text-3xl font-bold text-primary">{t("about.creds.network")}</p>
                <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-accent">
                  {t("about.creds.networkKicker")}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("about.creds.networkBody")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />


      <section className="bg-secondary py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">{t("about.apart.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("about.apart.sub")}</p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {apart.slice(0, 3).map((k) => (
              <div key={k} className="rounded-md bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center gap-2 text-accent">
                  <CheckCircle2 className="h-4 w-4" />
                  <h3 className="font-serif text-base font-semibold text-primary">{t(`about.apart.${k}.title`)}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(`about.apart.${k}.body`)}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-5 md:grid-cols-2 lg:mx-auto lg:max-w-3xl">
            {apart.slice(3).map((k) => (
              <div key={k} className="rounded-md bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center gap-2 text-accent">
                  <CheckCircle2 className="h-4 w-4" />
                  <h3 className="font-serif text-base font-semibold text-primary">{t(`about.apart.${k}.title`)}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(`about.apart.${k}.body`)}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="goldOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>

      <WaveDivider topColor="var(--secondary)" bottomColor="var(--background)" />

      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="text-center font-serif text-3xl font-bold text-primary sm:text-4xl">
            {t("about.commit.title")}
          </h2>

          <div className="mt-10 rounded-md border border-border bg-card p-7 shadow-sm">
            <div className="flex items-center gap-2 text-accent">
              <ShieldCheck className="h-5 w-5" />
              <h3 className="font-serif text-lg font-semibold text-primary">{t("about.commit.cs.title")}</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("about.commit.cs.body")}</p>
            <div className="mt-5 rounded-md bg-secondary/70 p-4">
              <p className="text-sm leading-relaxed text-muted-foreground">
                <Sparkles className="mr-2 inline h-3.5 w-3.5 text-accent" />
                <strong className="text-primary">{t("about.commit.partner.lead")}</strong>{" "}
                {t("about.commit.partner.body")}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <div className="rounded-md border border-border bg-card p-6 text-center shadow-sm">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Award className="h-5 w-5" />
              </div>
              <h3 className="mt-3 font-serif text-base font-semibold text-primary">{t("about.commit.roof.title")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("about.commit.roof.body")}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-6 text-center shadow-sm">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Sun className="h-5 w-5" />
              </div>
              <h3 className="mt-3 font-serif text-base font-semibold text-primary">{t("about.commit.solar.title")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("about.commit.solar.body")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden py-16 text-primary-foreground sm:py-24">
        <img
          src={aboutCta.src}
          srcSet={aboutCta.srcSet}
          sizes="100vw"
          alt="The AMR 3 crew with the owner shaking hands in front of a finished roof and solar installation at sunset"
          width={1920}
          height={1080}
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.82)] via-[oklch(0.18_0.03_50/0.68)] to-[oklch(0.18_0.03_50/0.44)]" />
        <div className="absolute left-0 right-0 top-0 z-10">
          <WaveDivider flip color="var(--background)" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 pt-12 text-center sm:px-6">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">{t("about.cta.title")}</h2>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-5 w-5" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("hero.cta1")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
